import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { issueCategories, issueTypes } from '../../data/businessSectors';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  FileText,
  Target,
  Tag
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import SolutionReviewModal from './SolutionBank/SolutionReviewModal';
import { normalizeBusinessRole } from '../../lib/role';

// D17 — client role gate (defense-in-depth; backend authoritative). Consumes the canonical Web role
// normalizer (lib/role.js): administrator + group_administrator (+ variants) collapse to 'admin', so the
// semantic approver set is {admin, manager}.

const SolutionBankManager = () => {
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const { t } = useLanguage();
  const canApprove = ['admin', 'manager'].includes(normalizeBusinessRole(user?.role));
  const [reviewTarget, setReviewTarget] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSolution, setEditingSolution] = useState(null);

  const [solutionForm, setSolutionForm] = useState({
    issueCategory: '',
    issueType: '',
    keywords: '',
    resolutionSteps: '',
    title: '',
    description: '',
    difficulty: 'medium', // New field
    rawInput: '', // For AI processing
    tags: '', // Enhanced tagging
    submittedBy: '', // Track who submitted
    status: 'draft' // draft, pending_approval, approved, rejected
  });

  const [solutions, setSolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);
  const [pendingSolutions, setPendingSolutions] = useState([]);

  const businessId = currentBusiness?.brandId;

  const loadSolutions = React.useCallback(() => {
    if (!businessId) return;
    setLoadingSolutions(true);
    makeAuthenticatedRequest(`/api/v1/solutions/${businessId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setSolutions(data.solutions || []))
      .catch(err => console.error('Failed to load solutions', err))
      .finally(() => setLoadingSolutions(false));
  }, [businessId, makeAuthenticatedRequest]);

  const loadPending = React.useCallback(() => {
    if (!businessId) return;
    makeAuthenticatedRequest(`/api/v1/solutions/${businessId}?status=pending_approval`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setPendingSolutions(
        (data.solutions || []).map(s => ({
          id: s.id,
          title: s.title || s.intent || t('st.sbm.untitled'),
          description: s.description || s.context_summary || '',
          issueCategory: s.issueCategory || s.category || '',
          submittedAt: s.submitted_at || s.created_at || s.last_updated || null,
        }))
      ))
      .catch(err => console.error('Failed to load pending solutions', err));
  }, [businessId, makeAuthenticatedRequest]);

  useEffect(() => { loadSolutions(); loadPending(); }, [loadSolutions, loadPending]);

  // D17 — direct one-click Approve/Reject removed here. Approval/rejection/return happen only in the
  // canonical full SolutionReviewModal (opened below), so this manager surface is no longer a second
  // one-click approval path. The backend re-validates every rule regardless of client.

  const resetForm = () => {
    setSolutionForm({
      issueCategory: '',
      issueType: '',
      keywords: '',
      resolutionSteps: '',
      title: '',
      description: '',
      difficulty: 'medium',
      rawInput: '',
      tags: '',
      submittedBy: '',
      status: 'draft'
    });
    setEditingSolution(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!solutionForm.title || !solutionForm.description || !solutionForm.resolutionSteps) {
      toast({ title: t('st.sbm.missingInfo'), description: t('st.sbm.missingInfoDesc'), variant: "destructive" });
      return;
    }

    if (!businessId) {
      toast({ title: t('st.sbm.noBusiness'), description: t('st.sbm.noBusinessDesc'), variant: "destructive" });
      return;
    }

    try {
      if (editingSolution) {
        // Update existing — backend enforces role-appropriate field restrictions
        const resp = await makeAuthenticatedRequest(`/api/v1/solutions/${editingSolution.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: solutionForm.title,
            description: solutionForm.description,
            resolution: solutionForm.resolutionSteps,
            category: solutionForm.issueCategory || 'General',
            keywords: solutionForm.keywords ? solutionForm.keywords.split(',').map(k => k.trim()) : [],
            tags: solutionForm.tags ? solutionForm.tags.split(',').map(t => t.trim()) : [],
            difficulty: solutionForm.difficulty || 'Medium',
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${resp.status}`);
        }
        toast({ title: t('st.sbm.updated'), description: t('st.sbm.updatedDesc') });
      } else {
        // Create new — always starts as pending_approval (enforced by backend)
        const resp = await makeAuthenticatedRequest('/api/v1/solutions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            business_id: businessId,
            title: solutionForm.title,
            description: solutionForm.description,
            resolution: solutionForm.resolutionSteps,
            category: solutionForm.issueCategory || 'General',
            keywords: solutionForm.keywords ? solutionForm.keywords.split(',').map(k => k.trim()) : [],
            tags: solutionForm.tags ? solutionForm.tags.split(',').map(t => t.trim()) : [],
            difficulty: solutionForm.difficulty || 'Medium',
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${resp.status}`);
        }
        toast({ title: t('st.sbm.submitted'), description: t('st.sbm.submittedDesc') });
      }

      // Reload from backend
      const listResp = await makeAuthenticatedRequest(`/api/v1/solutions/${businessId}`);
      if (listResp.ok) {
        const data = await listResp.json();
        setSolutions(data.solutions || []);
      }
      setIsDialogOpen(false);
      setEditingSolution(null);
      setSolutionForm({ issueCategory:'', issueType:'', keywords:'', resolutionSteps:'', title:'', description:'', difficulty:'medium', rawInput:'', tags:'', submittedBy:'', status:'pending_approval' });
    } catch (err) {
      toast({ title: t('st.sbm.submitFailed'), description: err?.message || t('st.sbm.networkError'), variant: "destructive" });
    }
  };

  const handleEdit = (solution) => {
    setSolutionForm({
      ...solution,
      resolutionSteps: solution.resolution || solution.resolutionSteps || '',
      issueCategory: solution.category || solution.issueCategory || '',
      keywords: Array.isArray(solution.keywords) ? solution.keywords.join(', ') : (solution.keywords || ''),
      tags: Array.isArray(solution.tags) ? solution.tags.join(', ') : (solution.tags || ''),
    });
    setEditingSolution(solution);
    setIsDialogOpen(true);
  };

  const handleDelete = async (solutionId) => {
    const solution = solutions.find(sol => sol.id === solutionId);
    if (!window.confirm(t('st.sbm.confirmDelete', { title: solution?.title }))) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/solutions/${solutionId}`, { method: 'DELETE' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      setSolutions(prev => prev.filter(s => s.id !== solutionId));
      toast({ title: t('st.sbm.deleted'), description: t('st.sbm.deletedDesc') });
    } catch (err) {
      toast({ title: t('st.sbm.deleteFailed'), description: err?.message, variant: "destructive" });
    }
  };


  const availableIssueTypes = solutionForm.issueCategory ? 
    issueTypes[solutionForm.issueCategory] || [] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('st.sbm.title')}</h2>
          <p className="text-gray-600">{t('st.sbm.subtitle')}</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={resetForm}
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('common.addNewSolution')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSolution ? t('st.sbm.editSolution') : t('common.addNewSolution')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-6">
              {/* AI-assisted authoring lives in the dedicated "AI Knowledge Builder" tab (real KB2
                  generation via the backend). This manager is for precise manual create/edit of
                  individual solutions — the previous client-side "AI Training Mode" mock was removed. */}

              {/* Solution Structure */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  {t('st.sbm.structure')}
                </h3>
              </div>

              {/* Basic Information */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('st.sbm.fTitle')}</Label>
                  <Input
                    id="title"
                    placeholder={t('st.sbm.fTitlePh')}
                    value={solutionForm.title}
                    onChange={(e) => setSolutionForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">{t('st.sbm.fDesc')}</Label>
                  <Textarea
                    id="description"
                    placeholder={t('st.sbm.fDescPh')}
                    value={solutionForm.description}
                    onChange={(e) => setSolutionForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>

              {/* Issue Classification */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issueCategory">{t('st.sbm.fCategory')}</Label>
                  <Select 
                    value={solutionForm.issueCategory}
                    onValueChange={(value) => setSolutionForm(prev => ({ 
                      ...prev, 
                      issueCategory: value,
                      issueType: '' // Reset issue type when category changes
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('st.sbm.categoryPh')} />
                    </SelectTrigger>
                    <SelectContent>
                      {issueCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="issueType">{t('st.sbm.fType')}</Label>
                  <Select 
                    value={solutionForm.issueType}
                    onValueChange={(value) => setSolutionForm(prev => ({ ...prev, issueType: value }))}
                    disabled={!solutionForm.issueCategory}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('st.sbm.typePh')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableIssueTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Enhanced Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="difficulty">{t('st.sbm.fDifficulty')}</Label>
                  <Select 
                    value={solutionForm.difficulty}
                    onValueChange={(value) => setSolutionForm(prev => ({ ...prev, difficulty: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('st.sbm.difficultyPh')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('st.sbm.diffLow')}</SelectItem>
                      <SelectItem value="medium">{t('st.sbm.diffMedium')}</SelectItem>
                      <SelectItem value="high">{t('st.sbm.diffHigh')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">{t('st.sbm.fTags')}</Label>
                  <Input
                    id="tags"
                    placeholder={t('st.sbm.tagsPh')}
                    value={solutionForm.tags || solutionForm.keywords}
                    onChange={(e) => setSolutionForm(prev => ({ 
                      ...prev, 
                      tags: e.target.value,
                      keywords: e.target.value // Keep backwards compatibility
                    }))}
                  />
                  <p className="text-sm text-gray-600">
                    {t('st.sbm.tagsHelp')}
                  </p>
                </div>
              </div>

              {/* Resolution Steps */}
              <div className="space-y-2">
                <Label htmlFor="resolutionSteps">{t('st.sbm.fResolution')}</Label>
                <Textarea
                  id="resolutionSteps"
                  placeholder={t('st.sbm.resolutionPh')}
                  value={solutionForm.resolutionSteps}
                  onChange={(e) => setSolutionForm(prev => ({ ...prev, resolutionSteps: e.target.value }))}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-gray-600">
                  {t('st.sbm.resolutionHelp')}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <Button onClick={handleSubmit} className="flex-1 bg-gradient-to-r from-green-500 to-blue-600">
                  <Save className="h-4 w-4 mr-2" />
                  {editingSolution ? t('st.sbm.updateSolution') : t('st.sbm.addSolution')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    resetForm();
                    setIsDialogOpen(false);
                  }}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Approvals Section */}
      {pendingSolutions.length > 0 && (
        <Card className="bg-amber-50/70 backdrop-blur-sm border-amber-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-amber-900">
              <span>{t('st.sbm.pendingApprovals')}</span>
              <Badge className="bg-amber-200 text-amber-900">{t('st.sbm.nPending', { count: pendingSolutions.length })}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingSolutions.map((pending) => (
                <div key={pending.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{pending.title}</h4>
                    <p className="text-sm text-gray-600 truncate">{pending.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {pending.issueCategory || t('st.sbm.uncategorized')}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {t('st.sbm.submittedOn', { date: formatInTenantTz(pending.submittedAt, { dateStyle: 'medium' }) })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {canApprove ? (
                      <Button
                        size="sm"
                        className="bg-yellow-600 hover:bg-yellow-700"
                        onClick={() => setReviewTarget(pending)}
                      >
                        Review Solution
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500">Pending manager review</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Solutions List */}
      <div className="grid gap-6">
        {solutions.length === 0 ? (
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('st.sbm.noSolutions')}</h3>
              <p className="text-gray-500 mb-6">{t('st.sbm.noSolutionsDesc')}</p>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('st.sbm.addFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          solutions.map((solution) => (
            <Card key={solution.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-3">{solution.title}</CardTitle>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        <Tag className="h-3 w-3 mr-1" />
                        {solution.category_label || solution.category || t('st.sbm.uncategorized')}
                      </Badge>
                      {solution.status && (
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          {solution.status}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <Target className="h-3 w-3 mr-1" />
                        {t('st.sbm.successRate', { rate: solution.success_rate || 0 })}
                      </Badge>
                      <Badge variant="outline">
                        {t('st.sbm.uses', { count: solution.usage_count || 0 })}
                      </Badge>
                    </div>
                    {solution.description && (
                      <p className="text-gray-600 mb-3">{solution.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(solution)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(solution.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  {/* Keywords */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('st.sbm.keywords')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(solution.keywords)
                        ? solution.keywords
                        : String(solution.keywords || '').split(',')
                      ).map((k) => (typeof k === 'string' ? k.trim() : k)).filter(Boolean).map((keyword, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Resolution Steps */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('st.sbm.resolutionSteps')}</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                        {solution.resolution || solution.resolutionSteps || '—'}
                      </pre>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t">
                    <span>{t('st.sbm.lastUpdated', { date: formatInTenantTz(solution.last_updated || solution.lastUpdated || solution.created_at, { dateStyle: 'medium' }) })}</span>
                    {/* Submitter resolved to a display NAME by the backend projection (submitted_by_name):
                        staff/customer name, or an AI/System origin label. Never the raw submitted_by id —
                        fall back to the localized "unknown" label, not the canonical uid. */}
                    <span>{t('st.sbm.addedBy', { name: solution.submitted_by_name || t('st.sbm.unknown') })}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {reviewTarget && canApprove && (
        <SolutionReviewModal
          solution={reviewTarget}
          businessId={currentBusiness?.brandId}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          toast={toast}
          t={t}
          onClose={() => setReviewTarget(null)}
          onDone={() => { setReviewTarget(null); loadPending(); loadSolutions(); }}
        />
      )}
    </div>
  );
};

export default SolutionBankManager;